import { TaskStatus } from 'constants/TaskStatus';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { EntityName, get, getCount, getDetailsForCorrectPagination, getPage, post } from 'stores/_services/dataService';
import { getConditions } from 'stores/_services/filterService';
import TaskStore, { ITask } from 'stores/task/TaskStore';
import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from '../BaseListStore';
import { IGetDataParams } from '../../types';

const SEARCH_FIELDS = ['name'];
const DEFAULT_FILTER = { search: '', taskType: null, taskStatus: null };

export default class StoreTaskList extends BaseListStore<TaskStore> {
    menuIds: string[] = [];
    activeLoaded = false;
    menuData = new Map<string, TaskStore>();

    get menuTasks(): (TaskStore | undefined)[] {
        return this.menuIds.map((id) => this.menuData.get(id));
    }

    get activeTasks(): (TaskStore | undefined)[] {
        return this.menuTasks.filter(
            (t) =>
                t?.task?.status?.toLowerCase() === TaskStatus.ACTIVE.toLowerCase() ||
                (t?.task?.previousStatus?.toLowerCase() === TaskStatus.ACTIVE.toLowerCase() &&
                    t?.task?.status?.toLowerCase() === TaskStatus.COMPLETED.toLowerCase())
        );
    }

    get finishedTasks(): (TaskStore | undefined)[] {
        return this.menuTasks.filter((t) => t && [TaskStatus.COMPLETED.toLowerCase(), TaskStatus.ERROR.toLowerCase()].includes(t?.task?.status?.toLowerCase()));
    }

    constructor() {
        super({
            itemStore: TaskStore,
            defaultFilter: DEFAULT_FILTER
        });

        makeObservable(this, {
            menuIds: observable,
            menuData: observable,
            activeLoaded: observable,
            findTask: action,
            getTasks: action,
            getMenuTasks: action,
            getTask: action,
            cancelTask: action,
            archiveTask: action,
            cancelAllTasks: action,
            archiveAllTasks: action,
            _updateMenuIds: action
        });
    }

    private _updateMenuData(data: ITask[]) {
        data.forEach((t: ITask) => {
            if (t) {
                const previousItem = this.menuData.get(t.id);
                // @ts-ignore ItemStore is guaranteed to be a class
                runInAction(() => {
                    this.menuData.set(t.id, new this.ItemStore(t, previousItem?.task));
                });
            }
        });
    }

    findTask(taskId: string): TaskStore | undefined {
        let result = this.menuTasks.find((t) => t?.task.id === taskId);

        if (!result) {
            result = this.findItem(taskId);
        }

        return result;
    }

    async getTasks(args = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        const shouldReset = this._updateListState(args);
        //TODO: replace getConditions with something better
        const conditions = getConditions(this.filter);
        conditions.push({ type: { neq: 'RTS' } });

        const options = {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                order: this.sort,
                searchFields: SEARCH_FIELDS,
                includeFields: ['id', 'name', 'user', 'type', 'status', 'startedOn', 'endedOn', 'userId', 'data', 'stats', 'progress'],
                where: conditions,
                include: {
                    relation: 'user',
                    scope: {
                        fields: ['id', 'email']
                    }
                }
            }
        };

        const { data } = await getPage(EntityName.USER_TASKS, options);
        const totalCount = await getCount(EntityName.USER_TASKS, {
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, this.skip);
        this._updateCount(data);

        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async getTasksListForCorrectPagination(args: IGetDataParams = {}): Promise<void> {
        const { filter, sortBy, sortDirection, pageSize, page } = args;

        const params = {
            filter: { ...(filter || this.filter), type: { neq: 'RTS' } },
            sortBy: sortBy || this.sortBy,
            sortDirection: sortDirection || this.sortDirection,
            pageSize: pageSize || this.pageSize,
            page: page || this.page,
            searchFields: SEARCH_FIELDS,
            includeFields: ['id', 'name', 'user', 'type', 'status', 'startedOn', 'endedOn', 'userId', 'data', 'stats', 'progress'],
            include: {
                relation: 'user',
                scope: {
                    fields: ['id', 'email']
                }
            }
        };

        const { data, totalCount } = await getDetailsForCorrectPagination<TaskStore>({
            params,
            entityName: EntityName.USER_TASKS
        });

        this.updateStateForCorrectPagination<TaskStore>({ args, data, totalCount });
    }

    async getMenuTasks(): Promise<void> {
        const { data } = await get('userTasks/active');

        if (this.activeLoaded) {
            const prev = this.menuTasks.map((t) => t?.task);
            const res = this.publishTaskStatusChanges(data, prev);

            //pull task one final time after everything is done
            if (res.cancelledTaskIds.length) {
                res.cancelledTaskIds.forEach((taskId) => {
                    setTimeout(() => {
                        this.getTask(taskId);
                    }, 10000);
                });
            }
        }

        this._updateMenuData(data);
        this._updateMenuIds(data);
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.activeLoaded = true;
        });
    }

    async getTask(taskId: string): Promise<void> {
        const { data } = await get(`userTasks/${taskId}`);

        this._updateData(data);
    }

    async cancelTask(taskStore: TaskStore): Promise<void> {
        await taskStore.cancel();

        runInAction(() => {
            this.menuIds = this.menuIds.filter((id) => id !== taskStore.task.id);
        });

        // Fetch task one final time after everything has updated
        setTimeout(() => {
            this.getTask(taskStore.task.id);
        }, 10000);
    }

    async archiveTask(taskStore: TaskStore): Promise<void> {
        await taskStore.archive();
        runInAction(() => {
            this.menuIds = this.menuIds.filter((id) => id !== taskStore.task.id);
        });
    }

    async cancelAllTasks(): Promise<void> {
        await post('userTasks/cancelAll');
        runInAction(() => {
            this.menuIds = this.menuIds.filter((id) => !this.activeTasks.some((t) => t?.task.id === id));
        });
    }

    async archiveAllTasks(): Promise<void> {
        await post('userTasks/archiveAll');
        runInAction(() => {
            this.menuIds = this.menuIds.filter((id) => !this.finishedTasks.some((t) => t?.task.id === id));
        });
    }

    _updateMenuIds(data: ITask[] = []): void {
        for (let i = 0; i < data.length; i++) {
            if (data[i]) {
                this.menuIds[i] = data[i].id;
            }
        }
    }

    publishTaskStatusChanges(current: ITask[], previous?: (ITask | undefined)[]): { cancelledTaskIds: string[] } {
        const res: { cancelledTaskIds: string[] } = {
            cancelledTaskIds: []
        };

        const currentTasks = [...current];

        previous?.forEach((prev?: ITask) => {
            if (!prev) {
                return;
            }

            const index = currentTasks.findIndex((c) => c.id === prev.id);

            if (index < 0) {
                // task not found in current active list
                res.cancelledTaskIds.push(prev.id);
                return;
            }
        });

        return res;
    }
}
